import { useMemo } from 'react';

import type { ClearinghouseState, UserFills } from '@/lib/utils/hyper/types';
import type { TradeData } from '@/lib/types/tradeData';
import {
  generateMockPriceData,
  generateMockTradeData,
} from '@/lib/data/mockPositionData';
import { getDecimalPlaces } from '@/lib/utils/token.utils';

import ChartIntervalPicker from '../trade/Chart/ChartInterval';
import { useHyperCandle } from '@/lib/hooks/useHyperCandle';
import { Box } from '@chakra-ui/react';
import { InteractiveLineChart } from '../charts/InteractiveLineChart';
import { MAX_WIDTH_IN_WINDOW } from '@/lib/config/page';

export type ChartInterval = '1m' | '5m' | '15m' | '1h' | '4h' | '1d' | '1w';

const intervals: Array<{
  value: ChartInterval;
  label: string;
  timeGap: number;
}> = [
  { value: '1m', label: '1m', timeGap: 1 * 60 * 1000 },
  { value: '5m', label: '5m', timeGap: 5 * 60 * 1000 },
  { value: '15m', label: '15m', timeGap: 15 * 60 * 1000 },
  { value: '1h', label: '1h', timeGap: 1 * 60 * 60 * 1000 },
  { value: '4h', label: '4h', timeGap: 4 * 60 * 60 * 1000 },
  { value: '1d', label: '1d', timeGap: 1 * 24 * 60 * 60 * 1000 },
  { value: '1w', label: '1w', timeGap: 7 * 24 * 60 * 60 * 1000 },
];

function getStartTime(
  interval: ChartInterval,
  limit: number,
  endTime: number,
): number {
  const intervalConfig = intervals.find((i) => i.value === interval);
  if (!intervalConfig) {
    throw new Error(`Invalid interval: ${interval}`);
  }

  const { timeGap } = intervalConfig;
  const totalTimeSpan = timeGap * limit;
  return endTime - totalTimeSpan;
}

interface PositionDetailChartProps {
  position: ClearinghouseState['assetPositions'][0]['position'] | null;
  coinSymbol: string;
  filteredFills?: UserFills;
  tradeData?: TradeData[];
  interval?: ChartInterval;
  onIntervalChange?: (interval: ChartInterval) => void;
}
const LIMIT = 50;
export function PositionDetailChart({
  position,
  filteredFills,
  tradeData,
  coinSymbol,
  interval: propInterval,
  onIntervalChange,
}: PositionDetailChartProps) {
  // console.log(filteredFills, 'filteredFills')
  const coin = coinSymbol || position?.coin.replace('-PERP', '') || '';
  const entryPrice = Number.parseFloat(position?.entryPx || '0');
  const isLongPosition = Number.parseFloat(position?.szi || '0') > 0;
  const interval: ChartInterval = propInterval || '1m'; // 使用传入的间隔或默认值
  const leverage = position?.leverage.value || 1;
  const time = useMemo(() => {
    const endTime = Date.now();
    return {
      startTime: getStartTime(interval, LIMIT, endTime),
      endTime,
    };
  }, [interval]);
  const { data: candleData = [], refetch: refreshCandle } = useHyperCandle({
    coin,
    interval,
    startTime: time.startTime,
    endTime: time.endTime,
    limit: LIMIT,
  });
  // Generate mock price and trade data
  const mockData = useMemo(() => {
    const priceData = generateMockPriceData(coin, entryPrice, 24);
    const tradeData = generateMockTradeData(
      coin,
      priceData,
      isLongPosition,
      leverage,
    );
    return { priceData, tradeData };
  }, [coin, entryPrice, isLongPosition, leverage]);
  const precision = useMemo(() => {
    const first10Items = candleData.slice(0, 10);
    let maxPrecision = 0;
    // console.log('fir', first10Items)
    first10Items.forEach((item: any) => {
      const ohlcValues = [
        item.o.toString(),
        item.h.toString(),
        item.l.toString(),
        item.c.toString(),
      ];
      ohlcValues.forEach((value) => {
        const decimalPlaces = getDecimalPlaces(value);
        maxPrecision = Math.max(maxPrecision, decimalPlaces);
      });
    });
    // console.log('Calculated max precision:', maxPrecision)
    return maxPrecision;
  }, [candleData]);
  // console.log('Precision:', precision)
  // console.log('Candle Data:', candleData)
  return (
    <Box>
      <ChartIntervalPicker
        interval={interval}
        onIntervalChange={onIntervalChange}
      />
      <Box>
        {candleData.length > 0 ? (
          <InteractiveLineChart
            priceData={candleData}
            tradeData={tradeData}
            interval={interval}
            width={"100%"}
            height={258}
            precision={precision}
          />
        ) : (
          <Box h="258px" rounded="lg" bg="text.t1/10" />
        )}
      </Box>
    </Box>
  );
}
